import { Alert, Button, Checkbox, FieldBlock, Input, LoadingState, Modal, Textarea, useToast } from '@orbind/ui'
import { useMemo, useState } from 'react'
import {
  type TemplateKind,
  type TemplateResumo,
  useAplicarTemplate,
  useSalvarComoTemplate,
  useTemplates,
} from '../admin/template-data.ts'
import './templates-ui.css'

/**
 * Uso dos templates fora da Administração (SPEC-20260830-1225):
 *  - `SalvarComoTemplateModal` — captura a cadeia do item aberto;
 *  - `EscolherTemplateModal` — escolhe um template ou "em branco" na criação;
 *  - `UsarTemplateChecklistModal` — aplica um checklist dentro de um item.
 */

// ------------------------------------------------- salvar item como template

export function SalvarComoTemplateModal({
  workItemId,
  tituloDoItem,
  temFilhos,
  onFechar,
}: {
  workItemId: string
  tituloDoItem: string
  temFilhos: boolean
  onFechar: () => void
}) {
  const [nome, setNome] = useState(tituloDoItem)
  const [descricao, setDescricao] = useState('')
  const [incluirFilhos, setIncluirFilhos] = useState(true)
  const [resetStatus, setResetStatus] = useState(true)
  const salvar = useSalvarComoTemplate()
  const toast = useToast()

  return (
    <Modal
      open
      title="Salvar como template"
      onClose={onFechar}
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={nome.trim() === '' || salvar.isPending}
            onClick={() =>
              salvar.mutate(
                { workItemId, name: nome.trim(), description: descricao.trim(), incluirFilhos, resetStatus },
                {
                  onSuccess: (r) => {
                    toast.push({
                      tone: 'success',
                      message: `Template “${r.template.name}” salvo com ${r.itens} ${r.itens === 1 ? 'item' : 'itens'}.`,
                    })
                    onFechar()
                  },
                  onError: (e) => toast.push({ tone: 'danger', message: (e as Error).message }),
                },
              )
            }
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar template'}
          </Button>
        </>
      }
    >
      <div className="tplui__form">
        <p className="tplui__ajuda">
          O template guarda a estrutura deste item — descrição, resultado esperado, campos complementares, checklists,
          membros, etiquetas e as relações internas (inclusive critérios de aceite). Comentários, anexos e histórico
          <strong> não</strong> entram.
        </p>
        <FieldBlock label="Nome do template">
          {({ id }) => <Input id={id} value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />}
        </FieldBlock>
        <FieldBlock label="Descrição" hint="Aparece na hora de escolher o template.">
          {({ id }) => <Textarea id={id} rows={2} value={descricao} onChange={(e) => setDescricao(e.target.value)} />}
        </FieldBlock>
        {temFilhos ? (
          <Checkbox
            checked={incluirFilhos}
            onChange={(e) => setIncluirFilhos(e.target.checked)}
            label="Incluir toda a cadeia de filhos"
          />
        ) : null}
        <Checkbox
          checked={resetStatus}
          onChange={(e) => setResetStatus(e.target.checked)}
          label="Ao aplicar, começar no status inicial"
        />
        <p className="tplui__ajuda tplui__ajuda--fina">
          Os itens criados nascem no status inicial. Desligue para nascerem no mesmo status em que estão agora. A etapa
          guardada no template é sempre respeitada.
        </p>
        {salvar.error ? <Alert tone="danger">{(salvar.error as Error).message}</Alert> : null}
      </div>
    </Modal>
  )
}

// ----------------------------------------------- escolher template na criacao

/**
 * Escolha entre um template e "em branco" na hora de criar um Item de Trabalho.
 * Quem chama decide o que fazer: `onEmBranco` segue o fluxo antigo de criação e
 * `onAplicado` recebe o id do item raiz que o template criou.
 */
export function EscolherTemplateModal({
  parentWorkItemId,
  flowStepId,
  kanbanBoardId,
  onEmBranco,
  onAplicado,
  onFechar,
}: {
  parentWorkItemId?: string | null
  /** Etapa em que a RAIZ nasce (lane do Kanban). */
  flowStepId?: string | null
  /** Quadro em que a cadeia nasce: vale a visibilidade dele (SPEC-20261007-0934-visibilidade-padrao-do-quadro). */
  kanbanBoardId?: string
  onEmBranco: () => void
  onAplicado: (rootId: string) => void
  onFechar: () => void
}) {
  const lista = useTemplates('work_item')
  const aplicar = useAplicarTemplate()
  const toast = useToast()
  const [escolhido, setEscolhido] = useState<TemplateResumo | null>(null)
  const templates = lista.data?.templates ?? []

  const aplicarAgora = (t: TemplateResumo) => {
    setEscolhido(t)
    aplicar.mutate(
      {
        id: t.id,
        parentWorkItemId: parentWorkItemId ?? null,
        flowStepId: flowStepId ?? null,
        ...(kanbanBoardId ? { kanbanBoardId } : {}),
      },
      {
        onSuccess: (r) => {
          toast.push({
            tone: 'success',
            message: `Criado a partir de “${t.name}” — ${r.criados} ${r.criados === 1 ? 'item' : 'itens'}.`,
          })
          if (r.rootId) onAplicado(r.rootId)
        },
        onError: (e) => {
          setEscolhido(null)
          toast.push({ tone: 'danger', message: (e as Error).message })
        },
      },
    )
  }

  return (
    <Modal open title="Novo Item de Trabalho" onClose={onFechar} width={620}>
      <div className="tplui__escolha">
        <button type="button" className="tplui__opcao tplui__opcao--branco" onClick={onEmBranco} disabled={aplicar.isPending}>
          <i className="fas fa-file" aria-hidden />
          <span className="tplui__opcao-txt">
            <strong>Em branco</strong>
            <small>Começa um item vazio e você monta na hora.</small>
          </span>
        </button>

        {lista.isLoading ? <LoadingState label="Carregando templates…" /> : null}

        {templates.length > 0 ? (
          <>
            <p className="tplui__secao">A partir de um template</p>
            <div className="tplui__opcoes">
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`tplui__opcao${escolhido?.id === t.id ? ' tplui__opcao--carregando' : ''}`}
                  disabled={aplicar.isPending}
                  onClick={() => aplicarAgora(t)}
                >
                  <i className={escolhido?.id === t.id ? 'fas fa-spinner fa-spin' : 'fas fa-sitemap'} aria-hidden />
                  <span className="tplui__opcao-txt">
                    <strong>{t.name}</strong>
                    {t.description ? <small>{t.description}</small> : null}
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : !lista.isLoading ? (
          <p className="tplui__vazio">
            Nenhum template ainda. Monte um Item de Trabalho como quer e use <strong>“Salvar como template”</strong> no
            menu dele.
          </p>
        ) : null}
      </div>
    </Modal>
  )
}

// ------------------------------------------- novo checklist: branco ou template

/**
 * Novo checklist: pede o NOME e deixa escolher entre criar **em branco** ou a
 * partir de um **template**. Escolhendo template, abre a lista com um campo de
 * busca que filtra por nome enquanto se digita (pedido 2026-08-30).
 *
 * O nome informado manda: aplicando um template, a lista nasce com esse nome
 * (em branco, cai no nome do próprio template).
 */
export function EscolherChecklistModal({
  workItemId,
  onCriarEmBranco,
  onFechar,
  onAplicado,
}: {
  workItemId: string
  /** Cria a lista vazia com o nome informado. */
  onCriarEmBranco: (nome: string) => void
  onFechar: () => void
  onAplicado?: () => void
}) {
  const lista = useTemplates('checklist')
  const aplicar = useAplicarTemplate()
  const toast = useToast()
  const [nome, setNome] = useState('')
  const [origem, setOrigem] = useState<'branco' | 'template'>('branco')
  const [busca, setBusca] = useState('')
  const [escolhido, setEscolhido] = useState<TemplateResumo | null>(null)

  const templates = lista.data?.templates ?? []
  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (termo === '') return templates
    return templates.filter(
      (t) => t.name.toLowerCase().includes(termo) || t.description.toLowerCase().includes(termo),
    )
  }, [templates, busca])

  const podeCriar =
    nome.trim() !== '' && (origem === 'branco' || escolhido !== null) && !aplicar.isPending

  const criar = () => {
    if (!podeCriar) return
    if (origem === 'branco') {
      onCriarEmBranco(nome.trim())
      return
    }
    aplicar.mutate(
      { id: escolhido!.id, workItemId, checklistName: nome.trim() },
      {
        onSuccess: (r) => {
          toast.push({
            tone: 'success',
            message:
              r.criados > 0
                ? `Checklist “${nome.trim()}” criado com ${r.criados} ${r.criados === 1 ? 'item filho' : 'itens filhos'}.`
                : `Checklist “${nome.trim()}” criado.`,
          })
          onAplicado?.()
          onFechar()
        },
        onError: (e) => toast.push({ tone: 'danger', message: (e as Error).message }),
      },
    )
  }

  return (
    <Modal
      open
      title="Novo checklist"
      onClose={onFechar}
      width={540}
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={!podeCriar} onClick={criar}>
            {aplicar.isPending ? 'Criando…' : 'Criar checklist'}
          </Button>
        </>
      }
    >
      <div className="tplui__form">
        <FieldBlock label="Nome do checklist">
          {({ id }) => (
            <Input
              id={id}
              value={nome}
              placeholder="Ex.: Publicação"
              autoFocus
              onChange={(e) => setNome(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && podeCriar) criar()
              }}
            />
          )}
        </FieldBlock>

        <div className="tplui__origem" role="radiogroup" aria-label="Como criar o checklist">
          <button
            type="button"
            role="radio"
            aria-checked={origem === 'branco'}
            className={`tplui__origem-op${origem === 'branco' ? ' tplui__origem-op--on' : ''}`}
            onClick={() => setOrigem('branco')}
          >
            <i className="fas fa-list-ul" aria-hidden />
            <span className="tplui__opcao-txt">
              <strong>Em branco</strong>
              <small>Você escreve os itens na hora.</small>
            </span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={origem === 'template'}
            className={`tplui__origem-op${origem === 'template' ? ' tplui__origem-op--on' : ''}`}
            onClick={() => setOrigem('template')}
          >
            <i className="fas fa-clone" aria-hidden />
            <span className="tplui__opcao-txt">
              <strong>De um template</strong>
              <small>Traz a lista pronta da biblioteca.</small>
            </span>
          </button>
        </div>

        {origem === 'template' ? (
          <div className="tplui__busca-bloco">
            <Input
              value={busca}
              placeholder="Buscar template…"
              aria-label="Buscar template de checklist"
              onChange={(e) => setBusca(e.target.value)}
            />
            {lista.isLoading ? <LoadingState label="Carregando templates…" /> : null}
            {!lista.isLoading && templates.length === 0 ? (
              <p className="tplui__vazio">
                Nenhum template de checklist ainda. Crie um em <strong>Administração › Templates</strong>.
              </p>
            ) : null}
            {templates.length > 0 ? (
              <div className="tplui__opcoes tplui__opcoes--rolagem">
                {filtrados.length === 0 ? (
                  <p className="tplui__vazio">Nenhum template com “{busca.trim()}”.</p>
                ) : (
                  filtrados.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`tplui__opcao${escolhido?.id === t.id ? ' tplui__opcao--on' : ''}`}
                      aria-pressed={escolhido?.id === t.id}
                      onClick={() => {
                        setEscolhido(t)
                        // sem nome digitado ainda, o do template já serve de sugestão
                        if (nome.trim() === '') setNome(t.name)
                      }}
                    >
                      <i className={escolhido?.id === t.id ? 'fas fa-check-circle' : 'fas fa-list-check'} aria-hidden />
                      <span className="tplui__opcao-txt">
                        <strong>{t.name}</strong>
                        {t.description ? <small>{t.description}</small> : null}
                      </span>
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </Modal>
  )
}

export type { TemplateKind }
